import React from 'react'
import { Loading } from '@workwhile/ui'
import { Calendar, CheckCircle2, Clock, Hash, User } from 'lucide-react'
import {
  CommandPaletteOption,
  CopyableValue,
  DetailsDescription,
  DetailsTitle,
  formatDateTime,
  SectionTitleWithButton,
  WorkItemEntry,
} from '../CommandPalette_Utils'
import { useLegacyShiftDetails } from '../../../queries/shifts/useLegacyShiftDetails'
import { InfoTable } from '../../../components/InfoTable/InfoTable'
import { getLocalTimezone } from 'lib/time'

type WorkItemDetailsProps = {
  workItem: WorkItemEntry
  onNavigateToShift: (shiftId: string | number) => void
  onItemInteraction: (item: CommandPaletteOption) => void
}

export function WorkItemDetails({
  workItem,
  onNavigateToShift,
  onItemInteraction,
}: WorkItemDetailsProps) {
  const queryOptions = {
    staleTime: 1000 * 60 * 5, // 5 minutes
    placeholderData: undefined, // avoid stale data when toggling between shifts
  }
  const { data: shiftDetail, isLoading: isLoadingShiftDetail } =
    useLegacyShiftDetails(workItem.shiftId, 'detail', queryOptions)
  const timezone =
    shiftDetail?.location?.address?.timezone ?? getLocalTimezone()

  const handleSearch = () => {
    onItemInteraction(workItem)
    onNavigateToShift(workItem.shiftId)
  }

  return (
    <>
      <DetailsTitle>
        Work Item #{workItem.id}
        {isLoadingShiftDetail ? (
          <span style={{ marginLeft: '8px' }}>
            <Loading type="button" />
          </span>
        ) : null}
      </DetailsTitle>
      <DetailsDescription>
        <div>
          <SectionTitleWithButton
            title="Work Item Information"
            buttonText="Go To Shift"
            onButtonClick={handleSearch}
            buttonTitle="View the shift this work item belongs to"
          />
          <InfoTable>
            <InfoTable.Row
              icon={<Hash size={14} />}
              label="Work ID"
              value={
                <CopyableValue
                  value={workItem.id}
                  fieldName="Work ID"
                  item={workItem}
                  onItemInteraction={onItemInteraction}
                />
              }
            />
            <InfoTable.Row
              icon={<User size={14} />}
              label="User ID"
              value={
                <CopyableValue
                  value={workItem.workerId}
                  fieldName="User ID"
                  item={workItem}
                  onItemInteraction={onItemInteraction}
                />
              }
            />
            <InfoTable.Row
              icon={<Calendar size={14} />}
              label="Shift ID"
              value={
                <CopyableValue
                  value={workItem.shiftId}
                  fieldName="Shift ID"
                  item={workItem}
                  onItemInteraction={onItemInteraction}
                />
              }
            />
            <InfoTable.Row
              icon={<CheckCircle2 size={14} />}
              label="Status"
              value={workItem.status}
            />
            <InfoTable.Row
              icon={<Clock size={14} />}
              label="Started"
              value={formatDateTime(workItem.startedAt, timezone)}
            />
            <InfoTable.Row
              icon={<Clock size={14} />}
              label="Completed"
              value={formatDateTime(workItem.completedAt, timezone)}
            />
          </InfoTable>
        </div>
      </DetailsDescription>
    </>
  )
}
